/**
 * 9 位行政区划码（AABBCCDDD）层级工具：省级 / 市级 / 区县级联动与校验。
 */

export type AdminDivisionTier = 'province' | 'city' | 'district' | 'unknown'

export function digitsOnly(raw: string): string {
  return String(raw ?? '').replace(/\D/g, '')
}

/** 归一为 9 位：满 9 截前 9；6～8 位取前 6 补 000 */
export function normalizeAdminDivisionCodeToNine(raw: string): string {
  const digits = digitsOnly(raw)
  if (!digits) return ''
  if (digits.length >= 9) return digits.slice(0, 9)
  if (digits.length >= 6) return `${digits.slice(0, 6)}000`
  return digits
}

export function resolveAdminDivisionTier(nineRaw: string): AdminDivisionTier {
  const nine = normalizeAdminDivisionCodeToNine(nineRaw)
  if (!/^\d{9}$/.test(nine)) return 'unknown'
  const bb = nine.slice(2, 4)
  const cc = nine.slice(4, 6)
  const ddd = nine.slice(6, 9)
  if (bb === '00' && cc === '00' && ddd === '000') return 'province'
  if (bb !== '00' && cc === '00' && ddd === '000') return 'city'
  if (cc !== '00' && ddd === '000') return 'district'
  // 乡镇等并入区县级展示口径时，仍按区县下级处理
  if (ddd !== '000') return 'district'
  return 'unknown'
}

/** 由 9 位码推导上一级 9 位码（与后端 AdministrativeRegionLevelUtil 一致） */
export function resolveParentNineDigitCode(nineRaw: string): string | null {
  const nine = normalizeAdminDivisionCodeToNine(nineRaw)
  if (!/^\d{9}$/.test(nine)) return null
  const bb = nine.slice(2, 4)
  const cc = nine.slice(4, 6)
  const ddd = nine.slice(6, 9)
  if (bb === '00' && cc === '00' && ddd === '000') return null
  if (bb !== '00' && cc === '00' && ddd === '000') return `${nine.slice(0, 2)}0000000`
  if (cc !== '00' && ddd === '000') return `${nine.slice(0, 4)}00000`
  if (ddd !== '000') return `${nine.slice(0, 6)}000`
  return null
}

/**
 * 在上级下新增时，子码必须锁定的前缀：
 * - 省下增市：前 2 位（如贵州 52）
 * - 市下增区：前 4 位（如贵阳 5201）
 */
export function getChildCodeLockedPrefix(
  parentCode: string,
  addLevel: 'city' | 'district',
): string {
  const parentNine = normalizeAdminDivisionCodeToNine(parentCode)
  if (!/^\d{9}$/.test(parentNine)) return ''
  if (addLevel === 'city') return parentNine.slice(0, 2)
  return parentNine.slice(0, 4)
}

/** 输入时强制保留上级前缀，仅允许改后缀数字 */
export function applyLockedPrefixToCodeInput(
  parentCode: string,
  addLevel: 'city' | 'district',
  rawInput: string,
): string {
  const prefix = getChildCodeLockedPrefix(parentCode, addLevel)
  if (!prefix) {
    return digitsOnly(rawInput).slice(0, 9)
  }
  const rest = digitsOnly(rawInput)
  // 用户若整段粘贴且已带正确前缀，保留；否则强制前缀 + 去掉错误前缀后的后缀
  const suffix = rest.startsWith(prefix) ? rest.slice(prefix.length) : rest
  return (prefix + suffix).slice(0, 9)
}

export interface ChildCodeValidation {
  ok: boolean
  message?: string
}

/**
 * 校验子级区划码是否属于所选上级，且形态符合市级/区级。
 */
export function validateChildAdminDivisionCode(
  parentCode: string,
  childCode: string,
  addLevel: 'city' | 'district',
): ChildCodeValidation {
  const parentNine = normalizeAdminDivisionCodeToNine(parentCode)
  const childNine = normalizeAdminDivisionCodeToNine(childCode)
  if (!/^\d{9}$/.test(parentNine)) {
    return { ok: false, message: '上级行政区划代码无效' }
  }
  if (!/^\d{9}$/.test(childNine)) {
    return { ok: false, message: '行政区划代码须为 9 位数字' }
  }
  if (childNine === parentNine) {
    return { ok: false, message: '子级区划代码不能与上级相同' }
  }

  const prefix = getChildCodeLockedPrefix(parentNine, addLevel)
  if (!childNine.startsWith(prefix)) {
    return {
      ok: false,
      message:
        addLevel === 'city'
          ? `须以省级前缀 ${prefix} 开头（如贵州省下须为 ${prefix} 开头）`
          : `须以市级前缀 ${prefix} 开头`,
    }
  }

  const tier = resolveAdminDivisionTier(childNine)
  if (addLevel === 'city') {
    if (tier !== 'city') {
      return { ok: false, message: '市级代码形态须为 AABB00000（如 520100000）' }
    }
    const parentOfChild = resolveParentNineDigitCode(childNine)
    if (parentOfChild !== parentNine) {
      return {
        ok: false,
        message: `市级代码须直接隶属于上级省（期望上级 ${parentNine}）`,
      }
    }
  } else {
    if (tier !== 'district') {
      return { ok: false, message: '区级代码形态须为 AABBCC000（如 520102000）' }
    }
    const parentOfChild = resolveParentNineDigitCode(childNine)
    if (parentOfChild !== parentNine) {
      return {
        ok: false,
        message: `区级代码须直接隶属于上级市（期望上级 ${parentNine}）`,
      }
    }
  }
  return { ok: true }
}

/**
 * 编辑时：新区划码须仍挂在同一上级码下（省改市码、市改区码）。
 * nodeLevel：1 省 / 2 市 / 3 区县
 */
export function validateEditedAdminDivisionCode(
  parentCode: string | null | undefined,
  newCode: string,
  nodeLevel: 1 | 2 | 3,
): ChildCodeValidation {
  const childNine = normalizeAdminDivisionCodeToNine(newCode)
  if (!/^\d{9}$/.test(childNine)) {
    return { ok: false, message: '行政区划代码须为 9 位数字' }
  }
  const tier = resolveAdminDivisionTier(childNine)
  if (nodeLevel === 1) {
    if (tier !== 'province') {
      return { ok: false, message: '省级代码形态须为 AA0000000（如 520000000）' }
    }
    return { ok: true }
  }
  const parentNine = normalizeAdminDivisionCodeToNine(parentCode ?? '')
  if (!/^\d{9}$/.test(parentNine)) {
    return { ok: false, message: '缺少上级区划代码，无法校验层级' }
  }
  if (nodeLevel === 2) {
    return validateChildAdminDivisionCode(parentNine, childNine, 'city')
  }
  return validateChildAdminDivisionCode(parentNine, childNine, 'district')
}

export function codePlaceholderForParent(
  parentCode: string | undefined,
  addLevel: 'city' | 'district',
): string {
  if (!parentCode) {
    return addLevel === 'city' ? '请先选择省份，如 520100000' : '请先选择市，如 520102000'
  }
  const prefix = getChildCodeLockedPrefix(parentCode, addLevel)
  if (addLevel === 'city') {
    return `须 ${prefix} 开头的市级码，如 ${prefix}0100000`
  }
  return `须 ${prefix} 开头的区级码，如 ${prefix}02000`
}

/** 从地区树收集已占用的 9 位区划码（含节点名称，便于提示） */
export function collectOccupiedAdminDivisionCodes(
  nodes: Array<{ code?: string; title?: string; children?: unknown[] }>,
): Map<string, string> {
  const map = new Map<string, string>()
  function walk(list: Array<{ code?: string; title?: string; children?: unknown[] }>) {
    for (const n of list) {
      const nine = normalizeAdminDivisionCodeToNine(String(n.code ?? ''))
      if (/^\d{9}$/.test(nine) && !map.has(nine)) {
        map.set(nine, String(n.title ?? nine))
      }
      const children = n.children
      if (Array.isArray(children) && children.length) {
        walk(children as Array<{ code?: string; title?: string; children?: unknown[] }>)
      }
    }
  }
  walk(nodes)
  return map
}

/** 区划码是否已被占用（编辑时可排除当前节点自身码） */
export function findDuplicateAdminDivisionCode(
  occupied: Map<string, string>,
  code: string,
  excludeCode?: string | null,
): { duplicate: true; occupiedBy: string } | { duplicate: false } {
  const nine = normalizeAdminDivisionCodeToNine(code)
  if (!/^\d{9}$/.test(nine)) return { duplicate: false }
  const excludeNine = excludeCode ? normalizeAdminDivisionCodeToNine(excludeCode) : ''
  if (excludeNine && nine === excludeNine) return { duplicate: false }
  const occupiedBy = occupied.get(nine)
  if (occupiedBy != null) return { duplicate: true, occupiedBy }
  return { duplicate: false }
}
